/* hp.js: interactie voor de hp-secties (carrousels, slider, snel toevoegen). Thema-onafhankelijk. */
(() => {
  if (window.__hpLoaded) return;
  window.__hpLoaded = true;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Horizontale carrousel met pijlen (productrijen, tegels, kaarten) */
  class HpCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track) return;
      this.onScroll = () => this.update();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this.ro = new ResizeObserver(() => this.update());
      this.ro.observe(this.track);
      this.update();
    }
    disconnectedCallback() { this.ro && this.ro.disconnect(); }
    go(dir) {
      const item = this.track.children[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
      this.track.scrollBy({ left: dir * step * perView, behavior: reduce ? 'auto' : 'smooth' });
    }
    update() {
      const t = this.track, max = t.scrollWidth - t.clientWidth - 2;
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft >= max;
      this.toggleAttribute('data-overflow', max > 0);
    }
    scrollToItem(i) {
      const item = this.track.children[i];
      if (item) this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'auto' });
    }
  }
  if (!customElements.get('hp-carousel')) customElements.define('hp-carousel', HpCarousel);

  /* Slider met autoplay, bolletjes en pauze (hero, aankondigingsbalk) */
  class HpSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.pauseBtn = this.querySelector('[data-pause]');
      this.interval = (parseFloat(this.dataset.autoplay) || 0) * 1000;
      this.index = 0;
      this.paused = reduce || !this.interval || this.slides.length < 2;
      this.dots.forEach((d, i) => d.addEventListener('click', () => { this.goTo(i); this.restart(); }));
      this.pauseBtn && this.pauseBtn.addEventListener('click', () => this.togglePause());
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.st);
        this.st = setTimeout(() => this.sync(), 80);
      }, { passive: true });
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.sync();
      this.start();
    }
    disconnectedCallback() { this.stop(); }
    goTo(i, smooth = true) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      const s = this.slides[this.index];
      this.track.scrollTo({ left: s.offsetLeft - this.track.offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'auto' });
      this.mark();
    }
    sync() {
      const w = this.track.clientWidth || 1;
      this.index = Math.round(this.track.scrollLeft / w);
      this.mark();
    }
    mark() {
      this.dots.forEach((d, i) => d.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
      this.slides.forEach((s, i) => s.toggleAttribute('inert', i !== this.index));
    }
    start() {
      this.stop();
      if (this.paused) return;
      this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
    }
    stop() { clearInterval(this.timer); }
    restart() { this.start(); }
    togglePause() {
      this.paused = !this.paused;
      this.toggleAttribute('data-paused', this.paused);
      this.pauseBtn && this.pauseBtn.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      this.paused ? this.stop() : this.start();
    }
  }
  if (!customElements.get('hp-slider')) customElements.define('hp-slider', HpSlider);

  /* Theme editor: naar het juiste blok springen */
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    const slider = el.closest('hp-slider');
    if (slider && slider.slides) { slider.stop(); slider.goTo(slider.slides.indexOf(el.closest('[data-track] > *')), false); }
    const car = el.closest('hp-carousel');
    if (car && car.track) car.scrollToItem([...car.track.children].indexOf(el.closest('[data-track] > *')));
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const slider = e.target.closest('hp-slider');
    slider && slider.start && slider.start();
  });

  /* Snel toevoegen vanuit productkaarten: AJAX + toast, valt terug op gewone submit */
  const toast = () => document.querySelector('[data-hp-toast]');
  const updateCount = async () => {
    try {
      const cart = await (await fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart.js')).json();
      document.querySelectorAll('[data-hp-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (e) { /* stil */ }
  };
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-hp-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    btn && btn.classList.add('is-loading');
    try {
      const root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      const res = await fetch(root + 'cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('add failed');
      await updateCount();
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      const t = toast();
      if (t) {
        t.classList.add('is-visible');
        clearTimeout(t._h);
        t._h = setTimeout(() => t.classList.remove('is-visible'), 4000);
      }
    } catch (err) {
      form.removeAttribute('data-hp-add');
      form.submit();
    } finally {
      btn && btn.classList.remove('is-loading');
    }
  });

  /* Taalkeuze: meteen versturen */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-hp-locale]');
    if (sel) sel.form.submit();
  });

  /* Footer-kolommen: open op desktop, accordion op mobiel */
  const mqDesk = window.matchMedia('(min-width: 750px)');
  const setAcc = () => document.querySelectorAll('details[data-hp-acc]').forEach((d) => { d.open = mqDesk.matches; });
  mqDesk.addEventListener('change', setAcc);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', setAcc) : setAcc();
  document.addEventListener('shopify:section:load', setAcc);

  /* Mobiel menu: sluiten met Escape, body niet laten scrollen */
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!d.matches || !d.matches('details[data-hp-drawer]')) return;
    document.documentElement.style.overflow = d.open ? 'hidden' : '';
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-hp-drawer][open], details[data-hp-dropdown][open]').forEach((d) => d.removeAttribute('open'));
    document.documentElement.style.overflow = '';
  });
})();
